import { CKEditor } from '@ckeditor/ckeditor5-react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Box, Typography } from '@mui/material';
import { Bold, ClassicEditor, Essentials, Heading, Italic, Link, Mention, Paragraph, Undo } from 'ckeditor5';
import 'ckeditor5/ckeditor5.css';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { sendTestEmail } from '../../store/actions/email';
import Button from '../FormComponents/Button';
import TextField from '../FormComponents/TextField';
import Modal from '../Modal/Modal';
import ToastMessage from '../ToastMessage/ToastMessage';
import './SurveyForm.scss';

interface EmailProps {
  open: boolean;
  content?: string;
  subject?: string;
  done: (data: { emailContent: string; emailSubject: string }) => void;
  close: () => void;
  defaultContent?: string;
  language: string;
  error?: boolean;
  errorEmailSubject?: boolean;
  readonly?: boolean;
  surveyLink?: string;
}

function Email(props: EmailProps) {
  const dispatch = useDispatch();
  const editor = useRef<ClassicEditor>();
  const {
    open,
    content,
    subject,
    done,
    close,
    defaultContent,
    language,
    error,
    errorEmailSubject,
    readonly,
    surveyLink,
  } = props;
  const [emailContent, setEmailContent] = useState('');
  const [emailToTest, setEmailToTest] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');

  useEffect(() => {
    setEmailContent(content);
  }, [content, open]);

  useEffect(() => {
    setEmailSubject(subject);
  }, [subject, open]);

  const closePopup = () => {
    close();
    setEmailContent('');
    setEmailSubject('');
  };

  const testEmail = () => {
    if (!emailToTest) {
      return;
    }
    setLoading(true);
    dispatch(
      sendTestEmail({ email: emailToTest, content: emailContent, subject: emailSubject }, (res, message) => {
        setLoading(false);
        if (res) {
          setEmailToTest('');
          setToastType('success');
          setToast('Email sent successfully');
        } else {
          setToastType('error');
          setToast(message || 'Failed to send email');
        }
      }),
    );
  };

  const toolbarItems = ['undo', 'redo', '|', 'heading', 'bold', 'italic', 'link'];
  const plugins = [Bold, Essentials, Italic, Mention, Paragraph, Undo, Link, Heading];

  return (
    <>
      <Modal
        fullWidth
        open={open}
        onClose={closePopup}
        title={`Email in ${language}`}
        button={
          !readonly ? (
            <Button
              text='Done'
              onClick={() => {
                done({ emailContent, emailSubject });
                closePopup();
              }}
            />
          ) : (
            <></>
          )
        }
        data={
          <Box>
            <TextField
              label='Email subject'
              id='emailSubject'
              variant='outlined'
              value={emailSubject}
              onChange={e => setEmailSubject(e.target.value)}
              sx={{ width: '50%', mb: 2, mt: 2, border: errorEmailSubject ? '1px solid red' : 'none' }}
              disabled={readonly}
            />
            {!readonly && !!surveyLink && (
              <Box display='flex'>
                <Typography variant='caption'>Click to copy survey link</Typography>
                <ContentCopyIcon
                  key='copy'
                  sx={{
                    fontSize: 15,
                    cursor: 'pointer',
                    margin: '0px 5px 0px 2px',
                  }}
                  onClick={() => {
                    navigator.clipboard.writeText(surveyLink);
                    setToastType('success');
                    setToast('Survey link copied');
                  }}
                />
                <Typography variant='caption'>
                  or{' '}
                  <Typography
                    variant='caption'
                    color='#3d82d0'
                    fontWeight='bold'
                    sx={{ cursor: 'pointer' }}
                    onClick={() => {
                      const newContent = `${emailContent || ''} <br /> <a href=${surveyLink}>Survey link</a>`;
                      setEmailContent(newContent);
                      editor.current.setData(newContent);
                    }}
                  >
                    insert
                  </Typography>{' '}
                  directly
                </Typography>
              </Box>
            )}
            <Box
              style={{
                border: error && !emailContent ? '1px solid red' : 'none',
              }}
            >
              <CKEditor
                editor={ClassicEditor}
                config={{
                  toolbar: {
                    items: toolbarItems,
                  },
                  plugins,
                  initialData: emailContent || content,
                }}
                onChange={(_, editor) => setEmailContent(editor.getData())}
                disabled={readonly}
                onReady={editorRef => {
                  editor.current = editorRef;
                }}
              />
            </Box>

            {language !== 'English' && (
              <>
                <Typography color={'GrayText'} sx={{ mt: 2 }}>
                  English
                </Typography>
                <CKEditor
                  editor={ClassicEditor}
                  config={{
                    toolbar: {
                      items: toolbarItems,
                    },
                    plugins,
                    initialData: defaultContent,
                  }}
                  disabled
                />
              </>
            )}
            <Box display={'flex'} flexDirection='row' gap={3} alignItems={'center'} mt={3}>
              <TextField
                label='Email to test'
                id='email'
                variant='outlined'
                value={emailToTest}
                onChange={e => setEmailToTest(e.target.value)}
                sx={{ width: '50%' }}
              />
              <Button
                disabled={!emailContent || !emailToTest}
                text='Test Email'
                onClick={testEmail}
                loading={loading}
              />
            </Box>
          </Box>
        }
      />
      <ToastMessage
        toast={toast}
        close={() => setToast('')}
        messageType={toastType || 'success'}
        autoHideDuration={6000}
      />
    </>
  );
}

export default Email;
